import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField } from '@mui/material'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

interface ConfirmToggleDialogProps {
  open: boolean
  functionName: string
  enabling: boolean
  isSubmitting: boolean
  onCancel: () => void
  onConfirm: (reason: string | undefined) => void
}

const ConfirmToggleDialog = ({ open, functionName, enabling, isSubmitting, onCancel, onConfirm }: ConfirmToggleDialogProps) => {
  const { t } = useTranslation()
  const [reason, setReason] = useState('')

  useEffect(() => {
    if (open) setReason('')
  }, [open])

  const reasonRequired = !enabling
  const reasonInvalid = reasonRequired && reason.trim().length === 0

  const title = enabling
    ? t('command.toggle.enableTitle', 'Enable timer trigger')
    : t('command.toggle.disableTitle', 'Disable timer trigger')

  const body = enabling
    ? t('command.toggle.enableBody', 'Enable timer trigger "{{name}}"?', { name: functionName })
    : t('command.toggle.disableBody', 'Disable timer trigger "{{name}}"?', { name: functionName })

  const confirmLabel = enabling
    ? t('command.toggle.enable', 'Enable')
    : t('command.toggle.disable', 'Disable')

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>{body}</DialogContentText>
        <TextField
          autoFocus
          fullWidth
          multiline
          minRows={2}
          label={reasonRequired
            ? t('command.toggle.reasonRequired', 'Reason (required)')
            : t('command.toggle.reasonOptional', 'Reason (optional)')}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          error={reasonInvalid}
          helperText={reasonInvalid ? t('command.toggle.reasonError', 'Reason is required when disabling.') : ' '}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={isSubmitting}>
          {t('command.toggle.cancel', 'Cancel')}
        </Button>
        <Button
          onClick={() => onConfirm(reason.trim() ? reason.trim() : undefined)}
          color={enabling ? 'success' : 'warning'}
          variant="contained"
          loading={isSubmitting}
          disabled={reasonInvalid}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ConfirmToggleDialog
